import { useEffect, useState } from 'react';
import { eventsApi } from '../api/events';
import type { FieldErrors, RepeatInput, RepeatOption } from '../api/events';
import { TextField } from '../ui/Field';

/**
 * What the leader picked in the Repeats group, or null for the ordinary single event.
 *
 * Only the pattern. The weekday is not here because it is the start date's, and the end date is
 * held beside this rather than inside it — the options are re-costed whenever the end date moves,
 * so keeping them together would mean rebuilding the choice every keystroke.
 */
export type RepeatChoice = Pick<RepeatOption, 'cadence' | 'week'> | null;

/** How far ahead the end date is suggested, in months. A term, roughly. */
const SUGGESTED_MONTHS = 3;

/**
 * A first suggestion for the end date, three months out.
 *
 * Date arithmetic on a bare date: built and read back through the same local frame, so no zone is
 * involved and none can be got wrong. It is a suggestion the leader edits, not a rule — the rule
 * about which dates a repeat lands on lives on the server, and only there.
 */
function suggestUntil(startsAt: string): string {
  const [year, month, day] = startsAt.slice(0, 10).split('-').map(Number);
  const suggested = new Date(year, month - 1 + SUGGESTED_MONTHS, day);
  const pad = (n: number) => String(n).padStart(2, '0');

  return `${suggested.getFullYear()}-${pad(suggested.getMonth() + 1)}-${pad(suggested.getDate())}`;
}

/** The payload half: a repeat and its end date, or null when the event happens once. */
export function repeatPayload(choice: RepeatChoice, until: string): RepeatInput | null {
  return choice && until ? { cadence: choice.cadence, week: choice.week, until } : null;
}

/** The reason the form cannot be submitted on account of the repeat, or undefined. */
export function repeatIncompleteReason(choice: RepeatChoice, until: string): string | undefined {
  return choice && !until ? 'Say what date to stop repeating on.' : undefined;
}

function sameChoice(a: RepeatChoice, b: RepeatChoice) {
  return a?.cadence === b?.cadence && (a?.week ?? null) === (b?.week ?? null);
}

/**
 * The Repeats group on the new-event screen.
 *
 * Here rather than in `EventForm` for the same reason the thread audience is: a repeat is a fact
 * about this act of creating, not about the event. Every event a repeat makes is complete and
 * independent, so there is nothing for the edit screen to offer — editing one of them edits one of
 * them, which is the whole design.
 *
 * The choices come from the server, already costed and already in words. Nothing here knows that a
 * fifth Wednesday is only ever "the last one", or how many Mondays are left in term, and that is
 * deliberate: the projector that will actually create the events is the thing that should be
 * answering both.
 */
export function RepeatFields({
  unitId,
  startsAt,
  choice,
  onChoose,
  until,
  onUntil,
  fieldErrors,
}: {
  unitId: string;
  /** The wall-clock start off the form. Empty until the leader has set one. */
  startsAt: string;
  choice: RepeatChoice;
  onChoose: (choice: RepeatChoice, until: string) => void;
  until: string;
  onUntil: (until: string) => void;
  fieldErrors: FieldErrors;
}) {
  const [options, setOptions] = useState<RepeatOption[]>([]);

  // What the options are costed against: whatever the leader typed, or the suggestion they have
  // not seen yet. Picking a repeat is what writes the suggestion into the box, so the count beside
  // an option and the count in force after choosing it are always the same number.
  const effectiveUntil = until || (startsAt ? suggestUntil(startsAt) : '');

  useEffect(() => {
    if (!startsAt || !effectiveUntil) {
      setOptions([]);
      return;
    }

    let live = true;

    eventsApi
      .repeatOptions(unitId, startsAt, effectiveUntil)
      .then((loaded) => live && setOptions(loaded))
      // Silent, and the group collapses to "Does not repeat". A repeat is an extra; a unit whose
      // options could not be loaded should still be able to post the one event they came for.
      .catch(() => live && setOptions([]));

    return () => {
      live = false;
    };
  }, [unitId, startsAt, effectiveUntil]);

  if (!startsAt) {
    return (
      <p className="t-caption" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
        Set when it starts and you can repeat it — every week, or once a month.
      </p>
    );
  }

  const repeatError = fieldErrors.repeat?.[0];

  return (
    <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
      <legend className="t-label" style={{ color: 'var(--color-text-muted)' }}>
        Repeats
      </legend>

      <div className="flex flex-col" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
        <RepeatRadio
          label="Does not repeat"
          detail="One event, on the date above."
          checked={choice === null}
          onSelect={() => onChoose(null, until)}
        />

        {options.map((option) => (
          <RepeatRadio
            key={`${option.cadence}-${option.week ?? ''}`}
            label={option.label}
            // Said before it is agreed to, because each of these is a real event with a real
            // thread. A leader should know they are about to make thirteen of them.
            detail={
              option.unavailable ??
              `Creates ${option.count} ${option.count === 1 ? 'event' : 'events'}, the last on ${option.lastDateLabel}.`
            }
            checked={sameChoice(choice, option)}
            unavailable={option.unavailable !== null}
            onSelect={() => onChoose({ cadence: option.cadence, week: option.week }, effectiveUntil)}
          />
        ))}
      </div>

      {repeatError && (
        <p className="t-caption" role="alert" style={{ color: 'var(--color-status-blocked)', marginTop: 'var(--space-2)' }}>
          {repeatError}
        </p>
      )}

      {choice && (
        <div style={{ marginTop: 'var(--space-3)', maxWidth: '16rem' }}>
          <TextField
            label="Repeat until"
            type="date"
            required
            value={until}
            error={fieldErrors.repeatUntil?.[0]}
            onChange={(e) => onUntil(e.target.value)}
            hint="The last date to schedule on. Move it and the counts above follow."
          />
        </div>
      )}
    </fieldset>
  );
}

/**
 * One choice, as a card.
 *
 * An unavailable option stays on screen and states why, rather than disappearing: a repeat that
 * would run past the cap is an ordinary thing to have half-typed, and an option that vanishes when
 * the end date moves reads as a bug rather than as an answer.
 */
function RepeatRadio({
  label,
  detail,
  checked,
  onSelect,
  unavailable = false,
}: {
  label: string;
  detail: string;
  checked: boolean;
  onSelect: () => void;
  unavailable?: boolean;
}) {
  return (
    <label
      className="flex items-start gap-3"
      style={{
        background: 'var(--color-surface-raised)',
        border: checked
          ? '1.5px solid var(--color-brand-ink)'
          : '1.5px solid var(--color-border-subtle)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-3) var(--space-4)',
        minHeight: 'var(--tap-min)',
        cursor: unavailable ? 'not-allowed' : 'pointer',
        opacity: unavailable ? 0.6 : 1,
      }}
    >
      <input
        type="radio"
        name="repeat"
        checked={checked}
        disabled={unavailable}
        onChange={onSelect}
        style={{ marginTop: '4px' }}
      />
      <span>
        <span className="t-body-strong" style={{ color: 'var(--color-text-primary)', display: 'block' }}>
          {label}
        </span>
        <span className="t-caption" style={{ color: 'var(--color-text-secondary)' }}>
          {detail}
        </span>
      </span>
    </label>
  );
}
